import React, { useRef, useEffect } from 'react';
import axios from 'axios';
import Button from 'react-bootstrap/Button';
import { Container, InputGroup, Form } from 'react-bootstrap';
import Testimonials from './Testimonials';
import { useState } from 'react';
import Alert from './Alert';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useLanguage } from '../contexts/LanguageContext';

// Renamed component to match the filename.
// Destructure the onStepComplete prop directly:
const RegisterProfile = ({ complete, forced }) => {
  const L = useLanguage();
  const emailRef = useRef();
  const nameRef = useRef();
  const passwordRef = useRef();
  const usernameRef = useRef();
  
  // This function is called when the form is submitted
  const handleButtonClick = (e) => {
    e.preventDefault();
    if (!forced) {
      window.location.href = '/dashboard';
    } else {
      complete();
    }
  };

  const [platform, setPlatform] = useState('instagram');
  const [username, setUsername] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [functionError, setFunctionError] = useState("");
  const [functionSuccess, setFunctionSuccess] = useState("");

  // Prefill from /visualizer handoff (if user came through the wheel preview)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('visualizer_handoff');
      if (!raw) return;
      const { platform: p, username: u, ts } = JSON.parse(raw);
      // Only honor the handoff if it's fresh (within 30 minutes)
      if (p && u && Date.now() - (ts || 0) < 30 * 60 * 1000) {
        setPlatform(p);
        setUsername(u);
      }
      localStorage.removeItem('visualizer_handoff');
    } catch (e) { /* invalid JSON or no storage — skip */ }
  }, []);

  // Fetch existing linked profiles on mount
  useEffect(() => {
    const fetchExistingProfiles = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) return;
        const response = await axios.get('https://api.viralgpt.com/auth/profile', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const socials = response.data.profile?.socials;
        if (socials && Object.keys(socials).length > 0) {
          const existing = Object.values(socials).map(s => ({
            platform: s.platform,
            username: s.username,
            photo: s.photo,
            profileid: s.profileid
          }));
          setAccounts(existing);
        }
      } catch (err) {
        console.error('Error fetching existing profiles:', err);
      }
    };
    fetchExistingProfiles();
  }, []);

  const searchAccount = async (username) => {
    try {
		setSearching(true);
      const response = await axios.post('https://api.viralgpt.com/auth/search', {
        platform,
        username
      });
      setSearchResults(response.data.data);
    } catch (err) {
      setFunctionError(err.response?.data?.message || L.registerSocial.searchFailed);
    } finally {
      setSearching(false);
    }
  };


  const addAccount = async (username) => {
    try {
		setAdding(true);
      const cleanUsername = String(username || '').trim().replace(/^@/, '');
      const purchasePlan = new URLSearchParams(window.location.search).get('purchase') || null;
      const response = await axios.post('https://api.viralgpt.com/auth/link',
        {
          platform,
          username: cleanUsername,
          purchasePlan,
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      setAccounts([...accounts, { platform, username, photo: response.data.photo, profileid: response.data.profileid }]);
      setPlatform('instagram');
      setUsername('');
	    setAdding(false);
      setFunctionSuccess(L.registerSocial.linkedSuccess(username, platform));
      // Select the newly added profile
      await axios.post("https://api.viralgpt.com/auth/select", {
        profileid: response.data.profileid
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`
        }
      });

    } catch (err) {
      setFunctionError(err.response?.data?.error || err.response?.data?.message || L.registerSocial.profileNotFound);
	    setAdding(false);
    }
  };

  const removeAccount = async (index) => {
    setAccounts(accounts.filter((_, i) => i !== index));
	const response = await axios.post('https://api.viralgpt.com/auth/unlink', 
	  {
		profileid: accounts[index].profileid
      },
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      }
    );


  };



  return (
    <div>
      <Container className="stripe-card stripe-card--elevated stripe-card--static p-4 my-12 justify-content-center text-center max-w-lg" fluid style={{border: 'none'}}>
       
	
        <Form onSubmit={handleButtonClick}>
          <h1 className="text-instagram stripe-heading stripe-heading--lg mb-1">{L.registerSocial.heading}</h1>
          <small className="stripe-body stripe-body--sm stripe-text-muted mb-3 d-block">
            {L.registerSocial.subheading}
          </small>

          <Alert 
            variant="success" 
            message={functionSuccess} 
            show={!!functionSuccess} 
            onClose={() => setFunctionSuccess("")} 
          />

          <Alert 
            variant="danger" 
            message={functionError} 
            show={!!functionError} 
            onClose={() => setFunctionError("")} 
          />

          {/* List of added accounts */}
          <div className="mb-4">
            {accounts.map((account, index) => (
              <div key={index} className="stripe-card stripe-card--sm stripe-card--static flex items-center justify-between p-3 rounded-xl mb-2" style={{background: '#f6f9fc'}}>
                <div className="flex items-center">
					{account.photo && (
						<img 
							src={account.photo}
							className="w-10 h-10 rounded-full me-2 stripe-avatar-ring stripe-avatar-ring--sm" 
							onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
						/>
					)}
                  <i className={`fab fa-${account.platform} me-2 ${
                    account.platform === 'instagram' ? 'text-pink-600' : 
                    account.platform === 'youtube' ? 'text-red-600' :
                    'stripe-text-dark'
                  }`}></i>
                  <span className="stripe-body stripe-text-dark font-medium">{account.username}</span>
                </div>
                <button 
                  onClick={() => removeAccount(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>

          {/* Add new account form - hidden once a profile is linked */}
          {accounts.length === 0 && (
            <>
              <div className="mb-6 w-full" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', background: '#f6f9fc', borderRadius: '12px', padding: '6px'}}>
                <button
                  type="button"
                  onClick={() => setPlatform('instagram')}
                  className={`px-4 py-3 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    platform === "instagram" ? "bg-instagram text-white font-semibold" : "stripe-text-body hover:bg-gray-100"
                  }`}
                  style={platform === "instagram" ? {borderRadius: '10px', boxShadow: '0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)', gridColumn: 'span 2'} : {borderRadius: '10px', gridColumn: 'span 2'}}
                >
                  <i className="fab fa-instagram text-xl me-2"></i>
                  Instagram
                </button>
                <button 
                  type="button"
                  onClick={() => setPlatform('tiktok')}
                  className={`px-4 py-3 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    platform === "tiktok" ? "font-semibold" : "stripe-text-body hover:bg-gray-100"
                  }`}
                  style={platform === "tiktok" ? {borderRadius: '10px', background: '#0a2540', color: '#fff', boxShadow: '0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)'} : {borderRadius: '10px'}}
                >
                  <i className="fab fa-tiktok text-xl me-2"></i>
                  TikTok
                </button>
                <button
                  type="button" 
                  onClick={() => setPlatform('x')}
                  className={`px-4 py-3 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    platform === "x" ? "font-semibold" : "stripe-text-body hover:bg-gray-100"
                  }`}
                  style={platform === "x" ? {borderRadius: '10px', background: '#0a2540', color: '#fff', boxShadow: '0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)'} : {borderRadius: '10px'}}
                >
                  <span className={`${platform === 'x' ? 'bg-black' : 'bg-gray-500'} px-1 rounded-md text-white text-sm me-2`}>𝕏</span>
                  Twitter
                </button>
              </div>

              <div className="flex gap-2 mb-4">
                <InputGroup size="md">
                  <InputGroup.Text id="username-addon" className="py-3">
                    <i className="fa-solid fa-at" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={L.registerSocial.usernamePlaceholder}
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ''))}
                    aria-label={L.registerSocial.usernameAriaLabel}
                    aria-describedby="username-addon"
                  />
                </InputGroup>
                <Button 
                  variant="primary"
                  onClick={platform === 'youtube' ? () => searchAccount(username) : () => addAccount(username)}
                  disabled={adding || searching}
                  className="px-4 stripe-btn stripe-btn--primary"
                  type="button"
                >
                  {adding || searching ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                    </>
                  ) : platform === 'youtube' ? L.registerSocial.searchButton : L.registerSocial.linkButton}
                </Button>
              </div>
              {platform === 'youtube' && (
                <div className="mb-4">
                  {searchResults?.map((result, index) => (
                    <div key={index} className="stripe-card stripe-card--sm stripe-card--static p-3 rounded-xl mb-2 d-flex align-items-center justify-content-between" style={{background: '#f6f9fc'}}>
                      <div className="d-flex align-items-center">
                        <img src={"https://rizzup.net/proxy/" + encodeURIComponent(result.thumbnail[0].url.startsWith('//') ? 'https:' + result.thumbnail[0].url : result.thumbnail[0].url)} className="me-2 rounded-lg" onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }} />
                        <span className="stripe-body stripe-text-dark font-medium">{result.channelTitle}</span>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => {
                          addAccount(result.channelId);
                        }}
                        className="rounded-circle stripe-btn stripe-btn--primary"
                        size="sm"
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {accounts.length > 0 && (
            <Button
              variant="primary"
              type="submit"
              className="w-full py-3 stripe-btn stripe-btn--primary"
            >
              <strong>{L.registerSocial.continue}</strong> <i className="fas fa-arrow-right ms-2"></i>
            </Button>
          )}
        </Form>
		{!forced && (
        <small className="d-block stripe-text-muted mt-6">
          {L.registerSocial.skipStep}
        </small>
		)}
      </Container>

    </div>
  );
};

export default RegisterProfile;
